import {
  Button,
  Card,
  DataStatus,
  Form,
  FormField,
  openDialog,
  Tag,
  useLocalStorage,
} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {
  ActiveQuickPresetResponseBody,
  useGenerateDescriptionMutation,
  useGetActiveQuickPresetsQuery,
} from '@dms/api/sales';
import {useGetDescriptionGeneratorConfigQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {ApiError, TestIdProps, useNavigate} from 'shared/web';

import {AddQuickPresetDialog} from './AddQuickPresetDialog';

type DescriptionGeneratorForm = {
  includes: string[];
  additionalInstruction: string | null;
  quickPresets: ActiveQuickPresetResponseBody[];
};

export interface DescriptionFormFieldProps extends TestIdProps {
  vehicleId: string;
  onGenerate: (content: string) => void;
}

export function DescriptionGenerator(props: DescriptionFormFieldProps) {
  const localStorageKey = `descriptionGenerator_isExpanded_${props.vehicleId}`;
  const [isExpanded, setIsExpanded] = useLocalStorage(localStorageKey, true);
  const [generateDescription, {isLoading: isGenerating}] = useGenerateDescriptionMutation();
  const {data: descriptionGeneratorConfig, isLoading: isConfigLoading} =
    useGetDescriptionGeneratorConfigQuery({});
  const {data: activeQuickPresets, isLoading: isQuickPresetsLoading} =
    useGetActiveQuickPresetsQuery({vehicleId: props.vehicleId});

  const navigate = useNavigate();

  const handleGenerate = async (data: DescriptionGeneratorForm) => {
    if (isNil(descriptionGeneratorConfig)) {
      return;
    }
    try {
      const response = await generateDescription({
        vehicleId: props.vehicleId,
        body: {
          language: descriptionGeneratorConfig.language,
          includeModel: data.includes.includes('includeModel'),
          includeEngine: data.includes.includes('includeEngine'),
          includeFeatures: data.includes.includes('includeFeatures'),
          includeCondition: data.includes.includes('includeCondition'),
          includeBranch: data.includes.includes('includeBranch'),
          quickPresetIds: data.quickPresets.map((qp) => qp.id),
          length: descriptionGeneratorConfig.length,
          mentionText: descriptionGeneratorConfig.mentionText,
          additionalInstruction: data.additionalInstruction,
        },
      }).unwrap();

      props.onGenerate(response.content);
    } catch (error) {
      handleApiError(error as ApiError);
    }
  };

  const includeOptions = [
    {
      label: i18n.t('entity.advertisingPlatforms.labels.includeModelShort'),
      value: 'includeModel',
    },
    {
      label: i18n.t('entity.advertisingPlatforms.labels.includeEngineShort'),
      value: 'includeEngine',
    },
    {
      label: i18n.t('entity.advertisingPlatforms.labels.includeFeaturesShort'),
      value: 'includeFeatures',
    },
    {
      label: i18n.t('entity.advertisingPlatforms.labels.includeConditionShort'),
      value: 'includeCondition',
    },
    {
      label: i18n.t('entity.advertisingPlatforms.labels.includeBranchShort'),
      value: 'includeBranch',
    },
  ];

  return (
    <Card
      title={i18n.t('entity.advertisingPlatforms.labels.enableDescriptionGenerator')}
      isExpandable={false} // Hide chevron, we use switch instead
      isExpanded={descriptionGeneratorConfig?.enabled && isExpanded}
      hasSeparator={false}
      variant="inlineGrey"
      control={{
        type: 'switch',
        value: !!descriptionGeneratorConfig?.enabled && isExpanded,
        isDisabled: !descriptionGeneratorConfig?.enabled,
        onChange: setIsExpanded,
      }}
      actions={[
        {
          type: 'button',
          title: i18n.t('general.labels.settings'),
          variant: 'link',
          leftIcon: 'action/settings',
          onClick: () => navigate(settingsRoutes.advertisingPlatforms),
        },
      ]}
    >
      <DataStatus isLoading={isConfigLoading || isQuickPresetsLoading}>
        <Form<DescriptionGeneratorForm>
          defaultValues={{
            includes: Object.entries(descriptionGeneratorConfig ?? {})
              .filter(([key, value]) => key.includes('include') && value)
              .map(([key]) => key),
            additionalInstruction: descriptionGeneratorConfig?.additionalInstruction ?? null,
            quickPresets: activeQuickPresets ?? [],
          }}
          onSubmit={handleGenerate}
        >
          {(control, formApi) => {
            const quickPresets = formApi.watch('quickPresets');

            return (
              <VStack spacing={4}>
                <Text size="xSmall" color="tertiary">
                  {i18n.t('entity.advertisingPlatforms.labels.descriptionGeneratorDescription')}
                </Text>
                <FormField
                  name="includes"
                  control={control}
                  type="chips"
                  isMultiple
                  size="small"
                  options={includeOptions}
                  label={i18n.t('entity.advertisingPlatforms.labels.descriptionIncludes')}
                />
                <VStack spacing={1}>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('entity.advertisingPlatforms.labels.descriptionQuickPresetDescription')}
                  </Text>
                  <HStack spacing={1} align="center" wrap>
                    {quickPresets.map((preset) => (
                      <Tag
                        key={preset.id}
                        onDelete={() => {
                          if (!isGenerating) {
                            formApi.setValue(
                              'quickPresets',
                              quickPresets.filter((qp) => qp.id !== preset.id)
                            );
                          }
                        }}
                        label={preset.name}
                        colorScheme="purple"
                        data-testid={`quick-preset-tag-${preset.id}`}
                      />
                    ))}
                    <Button
                      variant="link"
                      leftIcon="content/add_circle"
                      isDisabled={isGenerating}
                      onClick={() =>
                        openDialog(
                          <AddQuickPresetDialog
                            selectedPresets={quickPresets}
                            onSelect={(value) =>
                              formApi.setValue('quickPresets', [
                                ...quickPresets.filter((qp) => qp.id !== value.id),
                                value,
                              ])
                            }
                          />,
                          {
                            size: 'small',
                            title: i18n.t(
                              'entity.advertisingPlatforms.labels.descriptionAddQuickPreset'
                            ),
                          }
                        )
                      }
                      title={i18n.t('entity.advertisingPlatforms.labels.descriptionAddQuickPreset')}
                    />
                  </HStack>
                </VStack>
                <HStack align="flex-end" justify="space-between" grow={3} spacing={4}>
                  <Box flexGrow={1}>
                    <FormField
                      name="additionalInstruction"
                      control={control}
                      type="textarea"
                      isResizable
                      label={i18n.t('entity.advertisingPlatforms.labels.additionalInstruction')}
                      tooltip={i18n.t(
                        'entity.advertisingPlatforms.labels.additionalInstructionTooltip'
                      )}
                    />
                  </Box>
                  <Button
                    variant="primary"
                    leftIcon={isGenerating ? 'action/ai_robot' : 'action/ai'}
                    type="submit"
                    isDisabled={isGenerating}
                    title={
                      isGenerating
                        ? i18n.t('entity.advertisingPlatforms.labels.generating')
                        : i18n.t('entity.advertisingPlatforms.labels.generate')
                    }
                  />
                </HStack>
              </VStack>
            );
          }}
        </Form>
      </DataStatus>
    </Card>
  );
}
